import cn from 'classnames'
import { memo } from 'react'

import { useFormField } from 'cng-common/features/form'
import { ReadOnlyField } from 'cng-common/features/form/components'
import { ValidationType } from 'cng-common/features/form/configs'
import { IFormField } from 'cng-common/features/form/models'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Checkbox } from 'core-components/library/checkbox'
import { Tooltip } from 'core-components/library/tooltip'

import styles from './YesNoCheckboxes.module.scss'

type TYesNoAnswer = 'Y' | 'N'

const ANSWERS: TYesNoAnswer[] = ['Y', 'N']

const ANSWER_LABELS: Record<TYesNoAnswer, string> = {
  Y: 'Yes',
  N: 'No',
}

interface IProps
  extends Pick<
    IFormField<TYesNoAnswer | null>,
    | 'field'
    | 'formKey'
    | 'defaultValue'
    | 'onChange'
    | 'onValidate'
    | 'isVisible'
    | 'isRequired'
    | 'isDisabled'
    | 'isReadOnly'
  > {
  label: string
}

export const YesNoCheckboxes = memo<IProps>(
  ({ label, isVisible = true, isRequired, isDisabled, isReadOnly, ...formFieldProps }) => {
    const { value, validationMessage, handleChange } = useFormField<TYesNoAnswer | null>({
      ...formFieldProps,
      isVisible,
      isRequired,
      isDisabled,
      isReadOnly,
      validationType: ValidationType.ONCHANGE,
    })

    if (!isVisible) {
      return null
    }

    if (isReadOnly) {
      return <ReadOnlyField label={label} value={value && ANSWER_LABELS[value]} />
    }

    return (
      <Tooltip
        disabled={!validationMessage}
        tipContent={() => <Translation text={validationMessage} htmlAvailable span />}
      >
        <fieldset
          className={cn(styles.question, {
            [styles.required]: isRequired,
            [styles.invalid]: validationMessage,
          })}
        >
          <legend>
            <Translation text={label} span />
          </legend>
          {ANSWERS.map((answer) => (
            <label key={answer} className={styles.answer}>
              <Checkbox
                className={styles.checkbox}
                label={<Translation text={ANSWER_LABELS[answer]} span />}
                checked={value === answer}
                disabled={isDisabled}
                onChange={() => {
                  if (value !== answer) {
                    handleChange(answer)
                  }
                }}
              />
            </label>
          ))}
        </fieldset>
      </Tooltip>
    )
  }
)

YesNoCheckboxes.displayName = 'YesNoCheckboxes'
